import { useEffect, useRef } from "react";

import type { BillboardDisplaySnapshot } from "../features/billboards/billboardDisplay";
import type { BillboardSlotId } from "../services/billboard-api/BillboardApi";
import { errors } from "../services/errors/errors";
import { useTrafficStore } from "../stores/useTrafficStore";
import { TrafficScene, type TrafficSceneSnapshot } from "./TrafficScene";

function sceneSnapshot(
  billboards: BillboardDisplaySnapshot[],
  billboardsInteractive: boolean,
): TrafficSceneSnapshot {
  const state = useTrafficStore.getState();
  return {
    phase: state.phase,
    lane: state.lane,
    targetSpeed: state.targetSpeed,
    displayedSpeed: state.displayedSpeed,
    profile: state.profile,
    elapsedSeconds: state.elapsedSeconds,
    distanceMiles: state.distanceMiles,
    remoteDrivers: state.remoteDrivers,
    relativeTripPositions: state.transportMode === "supabase",
    reducedMotion: state.reducedMotion,
    performanceTier: state.performanceTier,
    hornSequence: state.hornSequence,
    flashSequence: state.flashSequence,
    reactionSequence: state.reactionSequence,
    lastReaction: state.lastReaction,
    boundarySequence: state.boundarySequence,
    remoteEventSequence: state.remoteEventSequence,
    lastRemoteEvent: state.lastRemoteEvent,
    billboards,
    billboardsInteractive,
  };
}

type TrafficCanvasProps = {
  billboards: BillboardDisplaySnapshot[];
  billboardsInteractive: boolean;
  onBillboardClick: (slotId: BillboardSlotId) => void;
};

export function TrafficCanvas({
  billboards,
  billboardsInteractive,
  onBillboardClick,
}: TrafficCanvasProps) {
  const hostRef = useRef<HTMLDivElement>(null);
  const sceneRef = useRef<TrafficScene | null>(null);
  const destroyTimerRef = useRef<number | null>(null);
  const billboardsRef = useRef(billboards);
  const billboardsInteractiveRef = useRef(billboardsInteractive);
  const onBillboardClickRef = useRef(onBillboardClick);

  useEffect(() => {
    billboardsRef.current = billboards;
    billboardsInteractiveRef.current = billboardsInteractive;
    onBillboardClickRef.current = onBillboardClick;
  }, [billboards, billboardsInteractive, onBillboardClick]);

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;

    if (destroyTimerRef.current !== null) {
      window.clearTimeout(destroyTimerRef.current);
      destroyTimerRef.current = null;
    }

    if (!sceneRef.current) {
      sceneRef.current = new TrafficScene(
        host,
        () =>
          sceneSnapshot(
            billboardsRef.current,
            billboardsInteractiveRef.current,
          ),
        useTrafficStore.getState().inspectRemote,
        useTrafficStore.getState().setVisibleRemoteCount,
        (slotId) => onBillboardClickRef.current(slotId),
      );
      void sceneRef.current.init().catch((error: unknown) => {
        errors.capture(error, { component: "traffic_scene" });
        useTrafficStore.getState().setCanvasFailed();
      });
    }
    const scene = sceneRef.current;

    const handleVisibility = () => scene.setPaused(document.hidden);
    document.addEventListener("visibilitychange", handleVisibility);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibility);
      destroyTimerRef.current = window.setTimeout(() => {
        if (sceneRef.current === scene) {
          scene.destroy();
          sceneRef.current = null;
        }
      }, 0);
    };
  }, []);

  return (
    <div
      ref={hostRef}
      className="traffic-canvas"
      data-testid="traffic-canvas"
    />
  );
}
